<template>
    <div class="grid">
        <div>
            <div
                v-if="domains.length > 0"
                class="-space-y-px rounded-md bg-white"
            >
                <h2 class="text-lg font-medium text-iron-700">
                    {{ $t("event_types.event_detail.domains.title") }}
                </h2>

                <p class="text-sm tracking-[-0.01em] text-iron-500">
                    {{ $t("event_types.event_detail.domains.description") }}
                </p>

                <div class="pt-8">
                    <div
                        v-for="(domain, key) in domains"
                        :key="domain.id"
                        class="relative flex border p-4"
                        :class="{
                            'rounded-tl-md rounded-tr-md': key === 0,
                            'rounded-bl-md rounded-br-md':
                                domains.length === key + 1,
                            'opacity-50 cursor-not-allowed': !domain.can_be_used_for_emails,
                            'cursor-pointer hover:bg-iron-50 focus:outline-none': domain.can_be_used_for_emails,
                        }"
                        @click="selectDomain(domain)"
                    >
                        <input
                            :disabled="domain.can_be_used_for_emails === false"
                            :id="domain.id.toString()"
                            name="notification-method"
                            type="radio"
                            :checked="
                                selectedDomain &&
                                selectedDomain.id === domain.id
                            "
                            class="h-4 w-4 border-iron-300 text-z-orange-600 focus:ring-z-orange-600"
                            :class="{
                                'cursor-not-allowed': !domain.can_be_used_for_emails
                            }"
                        />
                        <span class="ml-5 flex flex-col">
                            <span
                                id="privacy-setting-0-label"
                                class="block text-sm font-medium"
                                >{{ domain.name }}</span
                            >
                        </span>
                    </div>
                </div>

                <div v-if="selectedDomain" class="grid gap-4 pt-8">
                    <div class="grid w-full max-w-sm items-center gap-1.5">
                        <Label for="domain">{{
                            $t("domains.email_username")
                        }}</Label>
                        <InputWithAddon
                            v-model="emailUsername"
                            :suffix="'@' + selectedDomain.name"
                        />
                    </div>
                </div>
            </div>
        </div>

        <Link v-if="domains.length === 0" href="/domains">
            <PromoBanner :is-paid="true">
                <template #icon>
                    <Globe class="text-[#E92F34]" :size="20"></Globe>
                </template>
                <template #title>
                    {{ $t("event_types.event_detail.domains.banner.title") }}
                </template>
                <template #description>
                    {{
                        $t(
                            "event_types.event_detail.domains.banner.description",
                        )
                    }}
                </template>
            </PromoBanner>
        </Link>
    </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import { onMounted, ref } from "vue";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { Label } from "@/components/ui/label";
import InputWithAddon from "@/components/ui/input-with-addon/InputWithAddon.vue";
import { watchThrottled } from "@vueuse/core";
import PromoBanner from "../../Atomic/PromoBanner.vue";
import { Link } from "@inertiajs/vue3";
import { Globe } from "lucide-vue-next";

const domains = ref([]);
const selectedDomain = ref<Record<string, any> | null>(null);
const emailUsername = ref("zaptime" as string);

const props = defineProps({
    eventType: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(["on-finish"]);

onMounted(async () => {
    await getDomains();
    const domain = domains.value.find(
        (domain) => domain.id === props.eventType.domain_id,
    );

    if(domain) {
        selectDomain(domain);
    }

    if (props.eventType.email_username) {
        emailUsername.value = props.eventType.email_username;
    }
});

function selectDomain(domain: any) {
    if (!domain.can_be_used_for_emails) {
        return;
    }

    if (selectedDomain.value && selectedDomain.value.id === domain.id) {
        selectedDomain.value = null;
        return;
    }

    selectedDomain.value = domain;
}

async function getDomains() {
    const response = await axios.get(route("app.domains.index-json"));

    domains.value = response.data.data;
}

watchThrottled(
    [selectedDomain, emailUsername],
    ([domain, emailUsername]) => {
        if (domain && emailUsername !== undefined && domain.can_be_used_for_emails) {
            assignDomain(domain.id);
        } else {
            detachDomain();
        }
    },
    { throttle: 500 },
);

function assignDomain(domainId: number) {
    router.post(
        route("app.event-types.domains.assign", {
            eventType: props.eventType.id,
        }),
        {
            domainId: domainId,
            emailUsername: emailUsername.value,
        },
        {
            preserveState: true,
            onSuccess: () => {
                emit("on-finish");
            },
        },
    );
}

async function detachDomain() {
    router.post(
        route("app.event-types.domains.assign", {
            eventType: props.eventType.id,
        }),
        {
            domainId: null,
        },
        {
            preserveState: true,
            onSuccess: () => {
                emit("on-finish");
            },
        },
    );
}
</script>
